import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as CryptoJS from 'crypto-js';

const COLOMBIA_COUNTRY_CODE = 'CO';


@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  private isUpdatingValidators = false; // Add a flag to prevent recursion

  generateHash(data: any): string {
    return CryptoJS.SHA256(JSON.stringify(data)).toString();
  }

  verifyHash(data: any, hash: string): boolean {
    const dataHash = this.generateHash(data);
    return dataHash === hash;
  }

  noZeroValidator(control: FormControl) {
    const isZero = control.value === '0000';
    return isZero ? { 'zeroValue': true } : null;
  }

  noWhitespaceValidator(control: FormControl) {
    const value = (control.value || '').toString().trim();
    const isValid = value.length > 0;
    return isValid ? null : { 'whitespace': true };
  }

  notEmptyAddressValidator(control: FormControl) {
    const isEmptyAddress = control.value && control.value.consecutivo === 0 && control.value.direccionCliente === '';
    return isEmptyAddress ? { 'emptyAddress': true } : null;
  }

  currentYearValidator(control: AbstractControl): ValidationErrors | null {
    const selectedDate = control.value;
    if (selectedDate) {
      const selectedYear = new Date(selectedDate).getFullYear();
      const currentYear = new Date().getFullYear();
      const previousYear = currentYear - 1;

      // Allow only the current year or the previous year
      if (selectedYear !== currentYear && selectedYear !== previousYear) {
        return { notCurrentOrPreviousYear: true }; // Return an error if the year doesn't match
      }
    }
    return null; // Return null if the date is valid
  }

  noZeroPaisValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === '00') {
      return { invalidPais: true }; // Return an error if the value is "00"
    }
    return null; // Return null if the value is valid
  }

  noZeroDepartamentoValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === '00') {
      return { invalidDepartamento: true }; // Return an error if the value is "00"
    }
    return null; // Return null if the value is valid
  }

  noZeroCiudadValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === '00000') {
      return { invalidCiudad: true }; // Return an error if the value is "00000"
    }
    return null; // Return null if the value is valid
  }

  updateValidators(codeCountry: string, formGroup: FormGroup, controls: { [key: string]: string[] }): void {
    if (this.isUpdatingValidators) {
      return; // Exit if already updating validators
    }

    this.isUpdatingValidators = true; // Set the flag to true

    const condition = codeCountry === COLOMBIA_COUNTRY_CODE;

    Object.keys(controls).forEach((controlName) => {
      const control = formGroup.get(controlName);
      if (control) {
        this.applyValidators(control, controlName, controls[controlName], condition);
      }
    });

    this.isUpdatingValidators = false; // Reset the flag
  }

  private applyValidators(control: AbstractControl, controlName: string, validatorsList: string[], condition: boolean): void {
    if (condition) {
      const validators = validatorsList.map((validator) => Validators[validator]);
      this.addCustomValidators(controlName, validators);
      control.setValidators(validators);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false }); // Prevent triggering valueChanges
  }

  private addCustomValidators(controlName: string, validators: any[]): void {
    if (['departamentoDestino', 'departamentoConsignatario', 'consigneeDeptControl', 'destinationDeptControl'].includes(controlName)) {
      validators.push(this.noZeroDepartamentoValidator);
    }
    if (['ciudadDestino', 'ciudadConsignatario', 'consigneeCityControl', 'destinationCityControl'].includes(controlName)) {
      validators.push(this.noZeroCiudadValidator);
    }
    if (['paisDestino', 'paisConsignatario'].includes(controlName)) {
      validators.push(this.noZeroPaisValidator);
    }
  }
}